// Gap remediation: turn license-gap groups into planner moves using the cheapest option that closes the gap.
import { parseCell } from "./matrix";
import { EXTRA_ADDONS, UNLICENSED_SKU, priceFor } from "./planner";

export const REQUIREMENT_ROWS = { entra_p1: "Entra ID Plan 1", entra_p2: "Entra ID Plan 2", intune_p1: "Intune Plan 1" };

const granted = (row, planId) => !!planId && ["check", "text"].includes(parseCell(row.cells?.[planId]).kind);
const rowsFor = (rows, missing) => {
  const names = new Set(missing.map((m) => REQUIREMENT_ROWS[m]?.toLowerCase()).filter(Boolean));
  return rows.filter((r) => r.type === "feature" && names.has(r.name.toLowerCase()));
};
const coversAll = (needed, planIds) => needed.every((row) => planIds.some((id) => granted(row, id)));
const addonPrice = (addons, name) => addons.find((a) => a.name.toLowerCase() === name.toLowerCase())?.price ?? null;

const subsets = (list) => list.reduce((acc, item) => acc.concat(acc.map((s) => [...s, item])), [[]]).filter((s) => s.length);

/** Cheapest standalone add-on combination covering the missing rows (null when none does). */
const cheapestExtras = (needed, addons) => {
  const need = new Set(needed.map((r) => r.name.toLowerCase()));
  let best = null;
  subsets(Object.keys(EXTRA_ADDONS)).forEach((combo) => {
    const covered = new Set(combo.flatMap((n) => EXTRA_ADDONS[n]).map((n) => n.toLowerCase()));
    if (![...need].every((n) => covered.has(n))) return;
    const prices = combo.map((n) => addonPrice(addons, n));
    if (prices.some((p) => p === null)) return;
    const cost = prices.reduce((s, p) => s + p, 0);
    if (!best || cost < best.cost) best = { extras: combo, cost };
  });
  return best;
};

/** Build one planner move per remediation group. `requirement` limits the groups to those missing that requirement; `group` to one exact group. */
export const buildRemediation = ({ gaps, plans, addons, rows, priceMode, requirement = null, group = null }) => {
  const merged = {};
  (gaps?.remediation || [])
    .filter((g) => !requirement || g.missing.includes(requirement))
    .filter((g) => !group || (g.from_sku === group.from_sku && g.missing.join(",") === group.missing.join(",")))
    .forEach((g) => {
      const m = (merged[g.from_sku] = merged[g.from_sku] || { ...g, users: 0, missing: [] });
      m.users += g.users;
      m.missing = [...new Set([...m.missing, ...g.missing])].sort();
    });
  const groups = Object.values(merged);
  const byId = Object.fromEntries(plans.map((p) => [p.id, p]));
  const moves = [];
  const notes = [];
  groups.forEach((g) => {
    const needed = rowsFor(rows, g.missing);
    if (!needed.length) return;
    const fromPlan = byId[g.from_plan];
    const currentUnit = fromPlan ? priceFor(fromPlan, priceMode) ?? 0 : 0;
    const options = [];
    // A) stack standalone add-ons on the current plan
    if (fromPlan) {
      const ex = cheapestExtras(needed, addons);
      if (ex) options.push({ kind: "extras", unit: currentUnit + ex.cost, move: { to_plan: null, to_addons: [], to_extras: ex.extras }, label: ex.extras.join(" + ") });
      // B) suite add-on plans that require this base
      plans
        .filter((p) => p.enabled && p.addon && (p.requires || []).includes(g.from_plan) && coversAll(needed, [p.id]) && priceFor(p, priceMode) !== null)
        .forEach((p) => options.push({ kind: "suite", unit: currentUnit + priceFor(p, priceMode), move: { to_plan: null, to_addons: [p.id], to_extras: [] }, label: p.name }));
    }
    // C) move to a richer base plan (same family; any non-EDU family for unlicensed users)
    plans
      .filter((p) => p.enabled && !p.addon && p.price && p.id !== g.from_plan && (fromPlan ? p.group === fromPlan.group : p.group !== "edu") && coversAll(needed, [p.id]) && priceFor(p, priceMode) !== null)
      .forEach((p) => options.push({ kind: "plan", unit: priceFor(p, priceMode), move: { to_plan: p.id, to_addons: [], to_extras: [] }, label: p.name }));
    if (!options.length) {
      notes.push(`${g.sku_name}: no priced option covers ${g.missing.map((m) => REQUIREMENT_ROWS[m]).join(" + ")}`);
      return;
    }
    options.sort((a, b) => a.unit - b.unit || (a.kind === "plan" ? -1 : 1));
    const best = options[0];
    moves.push({
      from_sku: g.from_sku === UNLICENSED_SKU ? UNLICENSED_SKU : g.from_sku,
      from_plan: g.from_plan,
      seats: g.users,
      ...best.move,
      remediation: { missing: g.missing, users: g.users, option: best.label, unit: best.unit, alternatives: options.slice(1, 3).map((o) => ({ label: o.label, unit: o.unit })) },
    });
  });
  return { moves, notes };
};
